import theme from '@app/theme';
import { DateTimeRangePicker } from '@components/date/DateTimeRangePicker';
import SchedulesIcon from '@icons/schedules.svg';
import { rgba } from '@utils/color.utils';
import { makeNgComponent } from '@utils/ng.utils';
import { DurationInputArg2 } from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { DateFilterValue } from './DateFilterValue';

const Styles = styled.div.attrs((props) => ({ className: 'app-date-filter' }))`
    &&&.app-date-filter {
        //font-size: 1rem;
        padding: 1.57em 0;

        .most-recent {
            .title {
                font-style: normal;
                font-weight: 400;
                font-size: 0.71em;
                line-height: 1em;
                text-transform: uppercase;
                color: ${theme.colors.road_grey_800};
                padding: 0.28em 0.42em;
            }
        }

        .most-recent-items {
            display: flex;
            align-items: baseline;
            gap: 0.28em;
        }

        .exact {
            margin-top: 1.14em;

            .app-date-time-range-picker {
                margin-top: 0.5em;
            }
        }

        .date-filter-button {
            display: flex;
            width: max-content;
            align-items: center;
            gap: 0.57em;
            cursor: pointer;

            font-style: normal;
            font-weight: 400;
            font-size: 0.71em;
            line-height: 1em;
            text-transform: uppercase;
            padding: 0.28em 0.42em;

            background: ${rgba(theme.colors.road_grey, 0.05)};
            color: ${theme.colors.road_grey_900};
            border: none;
            border-radius: 0.14em;

            &.selected {
                background: ${theme.colors.brand_primary_blue};
                color: white;

                svg {
                    fill: white;
                }
            }
        }
    }
`;

type MostRecentItem = { title: string; duration: `${number}_${DurationInputArg2}` };

type DateFilterProps = {
    value?: DateFilterValue;
    onChange: (value: DateFilterValue) => any;
    mostRecentItems: MostRecentItem[];
};

DateFilter.propTypes = {
    value: PropTypes.shape({
        from: PropTypes.instanceOf(Date),
        to: PropTypes.instanceOf(Date),
        relative: PropTypes.string,
    }),
    onChange: PropTypes.func,
    mostRecentItems: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            duration: PropTypes.string,
        }),
    ),
};

const defaultMostRecentItems: MostRecentItem[] = [
    { title: '5 MIN', duration: '5_minutes' },
    { title: 'H', duration: '1_hour' },
    { title: 'D', duration: '1_day' },
    { title: 'W', duration: '1_week' },
    { title: 'MO', duration: '1_month' },
    { title: '2 MO', duration: '2_month' },
    { title: '3 MO', duration: '3_month' },
    { title: '6 MO', duration: '6_month' },
];

export function DateFilter({
    value: externalValue,
    onChange,
    mostRecentItems = defaultMostRecentItems,
}: DateFilterProps) {
    const [value, setValue] = useState(() => externalValue);

    useEffect(() => {
        setValue(externalValue);
    }, [externalValue]);

    function isRecentItemSelected(item: MostRecentItem) {
        return value?.relative === item.duration;
    }

    function updateValue(value) {
        setValue(value);
        if (onChange) {
            onChange(value);
        }
    }

    const mode = value?.relative
        ? 'relative'
        : typeof value?.from !== 'undefined' || typeof value?.to !== 'undefined'
          ? 'exact'
          : 'none';

    function toggleRecentSelect(item: MostRecentItem) {
        updateValue(value?.relative === item.duration ? null : new DateFilterValue({ relative: item.duration }));
    }

    function toggleExactSelect() {
        updateValue(mode === 'exact' ? null : new DateFilterValue({ from: null, to: null }));
    }

    function handleExactSelect(from?: Date, to?: Date) {
        updateValue(new DateFilterValue({ from: from || null, to: to || null }));
    }

    return (
        <Styles className="app-date-filter">
            <div className="most-recent">
                <div className="title">Most recent</div>
                <div className="most-recent-items">
                    {mostRecentItems.map((item, index) => (
                        <div
                            key={index}
                            className={`date-filter-button most-recent-item ${
                                isRecentItemSelected(item) ? 'selected' : ''
                            }`}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleRecentSelect(item);
                            }}
                        >
                            {item.title}
                        </div>
                    ))}
                </div>
            </div>
            <div className="exact">
                <div
                    className={`date-filter-button ${mode === 'exact' ? 'selected' : ''}`}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleExactSelect();
                    }}
                >
                    <span>Specific Period</span>
                    <SchedulesIcon />
                </div>

                {mode === 'exact' && (
                    <DateTimeRangePicker
                        from={value?.from || undefined}
                        to={value?.to || undefined}
                        onChange={(from, to) => handleExactSelect(from, to)}
                    />
                )}
            </div>
        </Styles>
    );
}

makeNgComponent('dateFilter', DateFilter);
